import { NextApiRequest } from 'next'

export enum PaymentMethod {
  CC = 'CC',
  ACH = 'ACH',
  Plaid = 'Plaid',
  PayPal = 'PayPal',
  PayPal_Commerce = 'PayPal_Commerce',
  Venmo = 'Venmo',
  Stripe = 'Stripe',
  DW = 'DW',
}

export enum PaymentProcessor {
  STRIPE = 'STRIPE',
  BRAINTREE = 'BRAINTREE',
  WEPAY = 'WEPAY',
  PAYPAL_COMMERCE = 'PAYPAL_COMMERCE',
  AUTHORIZENET = 'AUTHORIZENET',
}

export interface Payer {
  id: number
  appId: number
  firstName: string
  lastName: string
  email: string
  address1: string
  address2?: string
  address3?: string
  address4?: string
  state: string
  city: string
  zip: string
  country: string
  createdDate: Date
  createdBy: object
}

export enum TransactionType {
  Donation = 'DONATION',
  Goods = 'GOODS',
  Service = 'SERVICE',
  Event = 'EVENT',
  Auction = 'AUCTION',
}

export enum MemberRole {
  Admin = 'ADMIN',
  Supporter = 'SUPPORTER',
}

export interface FraudDetailsStripeRequest {
  charityId: number
  campaignId: number
  transactionId?: number
  recurringId?: number
  isVirtualTerminal?: boolean
}

export interface FraudDetailsCCRequest {
  campaignUri: string
  campaignDescription: string
}

export type PaymentDetailsStripeRequest = {
  source?: string
  defaultSource?: boolean
  accountId?: string
}

export type PaymentDetailsCCRequest = {
  token: string
  cvv: string
  expirationMonth: string
  expirationYear: string
}

export type PaymentDetailsACHPlaidRequest = {
  publicToken: string
  accountId: string
}

export type PaymentDetailsPayPalCommerceRequest =
  | {
      orderId: string
    }
  | {
      agreementId: string
    }

export type PaymentDetailsVenmoRequest = {
  orderId: string
}

export type PaymentDetailsACHRequest = {
  accountNumber: string
  routingNumber: string
}

export type PaymentMethodRequest =
  | PaymentDetailsStripeRequest
  | PaymentDetailsCCRequest
  | PaymentDetailsACHPlaidRequest
  | PaymentDetailsPayPalCommerceRequest
  | PaymentDetailsVenmoRequest
  | PaymentDetailsACHRequest

export interface CreateChargeIntentRequestBody {
  firstName: string
  lastName: string
  email: string
  address1: string
  address2?: string
  address3?: string
  address4?: string
  city: string
  state: string
  zip: string | undefined
  country: string
  currency: string
  fraudDetails?: FraudDetailsStripeRequest | FraudDetailsCCRequest
  paymentMethod: PaymentMethod
  memberFirstName?: string
  memberLastName?: string
  memberRole?: MemberRole
  paymentDetails: PaymentMethodRequest
  type: TransactionType
  amount: number
  statementDescriptor?: string
  referenceId?: string
  recurringDetails?: {
    recurringUnit?: string
    recurUntil?: string
    recurringStartDate?: string
    recurringTimeZone?: string
  }
  reCaptchaToken: string | null
}

export interface ChargeIntentResponse {
  chargeIntent: {
    id: string
    amount: string
    client_secret: string
    payer: Payer
  }
}

export interface ChargeIntentErrorResponse {
  errors: object[]
  requestId: string
  status: number
  statusText: string
}

export interface CreateChargeIntentRequest extends NextApiRequest {
  body: CreateChargeIntentRequestBody
}

export interface DonateRequestBody {
  firstName: string
  lastName: string
  email: string
  address1: string
  address2?: string
  address3?: string
  address4?: string
  city: string
  state: string
  zip: string | undefined
  country: string
  currency: string
  fraudDetails?: FraudDetailsStripeRequest | FraudDetailsCCRequest
  paymentMethod: PaymentMethod
  memberFirstName?: string
  memberLastName?: string
  memberRole?: MemberRole
  paymentDetails: PaymentMethodRequest
  type: TransactionType
  amount: number
  statementDescriptor?: string
  referenceId?: string
  reCaptchaToken: string | null
}

export interface RecurringDonationRequestBody extends DonateRequestBody {
  recurringUnit: string
  recurUntil?: string
  recurringStartDate: string
  recurringTimeZone: string
}

interface FeeSchedule {
  fixed: {
    name: string
    fee: number | string
    type?: string
  }
  percent: {
    name: string
    fee: number | string
    type?: string
  }
}

export interface DonateResponse {
  id: number
  transaction: {
    id: number
    appId: number
    currency: string
    amount: number
    chargeCurrency: string
    chargeAmount: number
    chargeTotal: number
    chargeFees: {
      applied: unknown
      applicationFee: number
      processorFee: number
      feeSum: number
      donationLessFees: number
      schedule: {
        applicationFees: FeeSchedule[]
        processorFees: FeeSchedule[]
      }
      payer: unknown
    }
    processorObjectId: string
    referenceId: string
  }
  payer: Payer
  paymentToken: {
    name: string
    type: string
    source: string
    details: {
      expMonth: string
      expYear: string
    }
  }
  processor: {
    id: string
    name: string
  }
  paymentTokenHistory: unknown
  transactionHistory: unknown
  errors?: unknown[]
  message?: string
}

export interface DonateRequest extends NextApiRequest {
  body: DonateRequestBody
}
